// frontend/src/services/assignmentService.js
// Fetches a read-only, assignment-based view of drivers for a specific site (Phase 7 uses siteId).
import axios from 'axios';

const ALLOWED_TIME_BUCKETS = new Set(['today', 'scheduled', 'historical']);

const normalizeSourceParam = (source) => {
  const value = String(source || 'all').toLowerCase();
  if (value === 'agency') return 'agency';
  if (value === 'internal') return 'internal';
  return undefined;
};

/**
 * Fetch assignment rows for a specific site.
 * Supports assignment-status filtering plus optional Phase 09 time_bucket filtering.
 * Used by UPS assignment-based lists.
 */
export async function fetchSiteAssignments(
  siteId,
  { statuses = [], agencyId = '', search = '', source = 'all', timeBucket } = {}
) {
  if (siteId == null) return [];

  const params = {};

  // Keep same param semantics as your current backend expects
  if (Array.isArray(statuses) && statuses.length) {
    params.statuses = statuses.join(',');
  }
  if (agencyId) params.agency_id = agencyId;
  if (search) params.search = search;

  const sourceParam = normalizeSourceParam(source);
  if (sourceParam) {
    params.source = sourceParam;
  }

  // Phase 09: optional time_bucket
  if (timeBucket && ALLOWED_TIME_BUCKETS.has(timeBucket)) {
    params.time_bucket = timeBucket;
  }

  // IMPORTANT: match your backend route prefixing.
  // Using /ups/... style as per your existing implementation.
  const { data } = await axios.get(`/ups/sites/${Number(siteId)}/assignments`, { params });

  return Array.isArray(data) ? data : [];
}

/**
 * Phase 8.7B — orphaned / blocked assignments (UPS-admin only)
 * Fetches assignments that are no longer linked to a valid site or configuration.
 */
export async function fetchOrphanedAssignments(
  siteId,
  { statuses = [], agencyId = '', search = '', source = 'all', timeBucket } = {}
) {
  if (siteId == null) return [];

  const params = {};

  if (Array.isArray(statuses) && statuses.length) {
    params.statuses = statuses.join(',');
  }
  if (agencyId) params.agency_id = agencyId;
  if (search) params.search = search;

  const sourceParam = normalizeSourceParam(source);
  if (sourceParam) {
    params.source = sourceParam;
  }

  // Phase 09: Optional time bucket
  if (timeBucket && ALLOWED_TIME_BUCKETS.has(timeBucket)) {
    params.time_bucket = timeBucket;
  }

  const { data } = await axios.get(
    `/ups/sites/${Number(siteId)}/assignments/orphaned`,
    { params }
  );

  return Array.isArray(data) ? data : [];
}

// Phase 8.7C — cleanup-only actions (UPS-admin)
export async function completeAssignment(siteId, assignmentId) {
  const { data } = await axios.patch(
    `/ups/sites/${Number(siteId)}/assignments/${Number(assignmentId)}/complete`
  );
  return data?.assignment || null;
}

export async function cancelAssignment(siteId, assignmentId) {
  const { data } = await axios.patch(
    `/ups/sites/${Number(siteId)}/assignments/${Number(assignmentId)}/cancel`
  );
  return data?.assignment || null;
}

// Phase 8.7D — UPS approval (requested -> confirmed)
export async function approveAssignment(siteId, assignmentId) {
  const { data } = await axios.patch(
    `/ups/sites/${Number(siteId)}/assignments/${Number(assignmentId)}/approve`
  );
  return data?.assignment || null;
}

// Phase 9.1 / Step 2D — move an open assignment to another site
export async function moveAssignment(
  siteId,
  assignmentId,
  { target_site_id, target_status } = {}
) {
  const payload = {};

  if (target_site_id != null) {
    payload.target_site_id = Number(target_site_id);
  }
  if (target_status) {
    payload.target_status = target_status;
  }

  const { data } = await axios.patch(
    `/ups/sites/${Number(siteId)}/assignments/${Number(assignmentId)}/move`,
    payload
  );

  return data?.assignment || data || null;
}